import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Network, Trash2, Plug, Save } from "lucide-react";
import { matrixApi, matrixError } from "@/lib/matrixApi";

/** Gestor de los 8 proxies rotativos (pegar lista, probar, activar, borrar). */
export const MatrixProxies = ({ onClose, onCount }) => {
  const [list, setList] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(null);
  const touched = useRef(false);

  const load = async () => {
    try {
      const { data } = await matrixApi.get("/proxies");
      setList(data.proxies);
      onCount?.(data.proxies.filter((p) => p.enabled).length);
      if (!touched.current) {
        setText(data.proxies.map((p) => `${p.scheme}://${p.host}:${p.port}`).join("\n"));
      }
    } catch (e) { toast.error(matrixError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await matrixApi.post("/proxies", { text });
      setList(data.proxies);
      onCount?.(data.proxies.filter((p) => p.enabled).length);
      if (data.invalid?.length) toast.warning(`No entendí ${data.invalid.length} línea(s)`);
      toast.success(`${data.proxies.length} proxy(s) guardados`);
      touched.current = false;
      load();
    } catch (e) { toast.error(matrixError(e)); }
    finally { setBusy(false); }
  };

  const test = async (id) => {
    setTesting(id);
    try {
      const { data } = await matrixApi.post(`/proxies/${id}/test`);
      if (data.ok) toast.success(`Funciona · IP ${data.ip} · ${data.ms} ms`);
      else toast.error(`Falló: ${data.error}`);
      load();
    } catch (e) { toast.error(matrixError(e)); }
    finally { setTesting(null); }
  };

  const toggle = async (p) => {
    await matrixApi.patch(`/proxies/${p.id}?enabled=${!p.enabled}`);
    load();
  };
  const del = async (id) => {
    await matrixApi.delete(`/proxies/${id}`);
    touched.current = false;
    load();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-5"
      data-testid="matrix-proxies-modal"
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="glass-card forge-card p-5 w-full max-w-2xl max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <span className="font-heading text-white tracking-wider flex items-center gap-2">
            <Network className="w-4 h-4 text-red-400" /> MIS PROXIES
          </span>
          <button onClick={onClose} data-testid="matrix-proxies-close"
            className="text-white/50 hover:text-white">✕</button>
        </div>

        <p className="text-[11px] text-white/45 mb-2">
          Pega hasta 8 proxies, uno por línea. Formatos: <span className="font-mono">ip:puerto</span>,{" "}
          <span className="font-mono">ip:puerto:usuario:clave</span> o{" "}
          <span className="font-mono">usuario:clave@ip:puerto</span>. Se usan por turnos, uno en cada análisis.
          Las claves guardadas no se muestran, pero se conservan aunque vuelvas a guardar la lista.
        </p>
        <textarea data-testid="matrix-proxies-text" rows={5} value={text}
          onChange={(e) => { touched.current = true; setText(e.target.value); }}
          placeholder={"45.12.34.56:8000:usuario:clave\n45.12.34.57:8000:usuario:clave"}
          className="w-full glass-card px-3 py-2 mb-3 bg-black/50 text-white text-xs font-mono outline-none" />
        <button data-testid="matrix-proxies-save" onClick={save} disabled={busy}
          className="matrix-btn px-5 py-2.5 rounded-xl text-xs flex items-center gap-2">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Guardar lista
        </button>

        <div className="mt-4 glass-card divide-y divide-white/5">
          {list.length === 0 && (
            <p className="text-xs text-white/40 p-4">Todavía no has guardado proxies.</p>
          )}
          {list.map((p) => (
            <div key={p.id} className="px-3 py-2.5 flex items-center gap-3 text-xs"
              data-testid={`matrix-proxy-${p.id}`}>
              <button onClick={() => toggle(p)} data-testid={`matrix-proxy-toggle-${p.id}`}
                className={`w-9 h-5 rounded-full relative shrink-0 transition-colors ${
                  p.enabled ? "bg-red-600/80" : "bg-white/15"}`}>
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${
                  p.enabled ? "left-4" : "left-0.5"}`} />
              </button>
              <span className="font-mono text-white/85 truncate flex-1">
                {p.host}:{p.port}
                {p.username && <span className="text-white/35"> · {p.username}</span>}
              </span>
              {p.last_ip && <span className="text-emerald-400/80 font-mono shrink-0">{p.last_ip}</span>}
              {p.last_error && <span className="text-rose-400/80 shrink-0">error</span>}
              <span className="text-white/30 shrink-0">{p.uses}×</span>
              <button onClick={() => test(p.id)} data-testid={`matrix-proxy-test-${p.id}`}
                className="text-white/50 hover:text-red-300 shrink-0" title="Probar">
                {testing === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <Plug className="w-3.5 h-3.5" />}
              </button>
              <button onClick={() => del(p.id)} data-testid={`matrix-proxy-del-${p.id}`}
                className="text-white/50 hover:text-red-400 shrink-0">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
